import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  MessageSquare, Send, Mic, MicOff, Sparkles, Loader2, ChevronLeft, ChevronRight,
  Check, Mail, X, Copy, RefreshCw, FlaskConical
} from 'lucide-react';
import BulkSendProgress from './BulkSendProgress';

const API = '/api';

function EmailBubble({ data, version }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    const text = `Dear ${data.contact_name || 'Sir/Madam'},\n\n${data.email_body}\n`;
    const plainText = text.replace(/\*\*/g, '');
    const htmlBody = text
      .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
      .replace(/\n/g, '<br>');
    const html = `<div style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: #000000; line-height: 1.6;">${htmlBody}</div>`;
    try {
      navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        })
      ]);
    } catch {
      navigator.clipboard.writeText(plainText);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-[85%] space-y-1">
      <div className="text-[10px] text-slate-400 flex items-center gap-1.5 ml-1">
        <Sparkles className="w-3 h-3" />
        Version {version} &middot; {data.company_name}
        {data.asset_name && <span className="text-teal-500">&middot; {data.asset_name}</span>}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <div className="text-xs text-slate-500 border-b border-slate-100 pb-2 mb-2">
          To: <span className="font-medium text-slate-700">{data.contact_name}</span> &lt;{data.contact_email}&gt;
        </div>
        <p className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif" }}>
          Dear {data.contact_name || 'Sir/Madam'},{'\n\n'}
          {data.email_body}
        </p>
        <div className="flex justify-end mt-2">
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1 text-[10px] transition-colors ${copied ? 'text-emerald-500' : 'text-slate-400 hover:text-slate-600'}`}
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BulkEmailChat({ isOpen, onClose, selectedCompanyIds, stageFilter, onSendComplete }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [subject, setSubject] = useState('');
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const wantsListeningRef = useRef(false);

  // Generated emails state
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [genTaskId, setGenTaskId] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [genProgress, setGenProgress] = useState({ generated: 0, total: 0 });
  const [checkingStatus, setCheckingStatus] = useState(false);
  // AI Composer v2 controls — length, tone, paragraphs, CTA. Fed to the
  // backend so the personalization respects the user's preferences.
  // Defaults chosen for a balanced "safe" first draft; user can tweak
  // before regenerating.
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerLength, setComposerLength] = useState('medium'); // short | medium | detailed
  const [composerTone, setComposerTone] = useState('executive');   // formal | friendly | executive | scientific
  const [composerParagraphs, setComposerParagraphs] = useState(3);
  const [composerCta, setComposerCta] = useState('');

  const checkLatestTask = useCallback(async () => {
    setCheckingStatus(true);
    try {
      const res = await axios.get(`${API}/bulk-email/generate-latest`, { withCredentials: true });
      if (res.data.status === 'none') {
        toast.info('No generation tasks running');
      } else if (res.data.status === 'running') {
        setGenerating(true);
        setGenTaskId(res.data.task_id);
        setGenProgress({ generated: res.data.generated || 0, total: res.data.total || 0 });
        if (res.data.subject) setSubject(res.data.subject);
        // Start polling
        const poll = setInterval(async () => {
          try {
            const st = await axios.get(`${API}/bulk-email/generate-status/${res.data.task_id}`, { withCredentials: true });
            if (st.data.status === 'running') {
              setGenProgress({ generated: st.data.generated, total: st.data.total });
            }
            if (st.data.status === 'completed') {
              clearInterval(poll);
              const list = Object.values(st.data.emails || {});
              setGenerated(list);
              if (st.data.subject && !subject) setSubject(st.data.subject);
              setGenerating(false);
            }
            if (st.data.status === 'failed') {
              clearInterval(poll);
              toast.error('Generation failed');
              setGenerating(false);
            }
          } catch { /* keep polling */ }
        }, 2000);
        toast.info(`Generation in progress: ${res.data.generated}/${res.data.total}`);
      } else if (res.data.status === 'interrupted') {
        // Backend restarted mid-generation — offer resume
        setGenTaskId(res.data.task_id);
        if (res.data.subject) setSubject(res.data.subject);
        setGenProgress({ generated: res.data.generated || 0, total: res.data.total || 0 });
        const doResume = window.confirm(
          `Generation was interrupted (${res.data.generated}/${res.data.total} done). Resume where it left off?`
        );
        if (doResume) {
          setGenerating(true);
          const resumeRes = await axios.post(`${API}/bulk-email/resume-generation/${res.data.task_id}`, {}, { withCredentials: true });
          toast.success(`Resuming: ${resumeRes.data.remaining} emails remaining`);
          // Start polling
          const poll = setInterval(async () => {
            try {
              const st = await axios.get(`${API}/bulk-email/generate-status/${res.data.task_id}`, { withCredentials: true });
              if (st.data.status === 'running') {
                setGenProgress({ generated: st.data.generated, total: st.data.total });
              }
              if (st.data.status === 'completed') {
                clearInterval(poll);
                const list = Object.values(st.data.emails || {});
                setGenerated(list);
                if (st.data.subject && !subject) setSubject(st.data.subject);
                setGenerating(false);
              }
              if (st.data.status === 'failed') {
                clearInterval(poll);
                toast.error('Generation failed');
                setGenerating(false);
              }
            } catch { /* keep polling */ }
          }, 2000);
        } else {
          // Show what's already generated
          const list = Object.values(res.data.emails || {});
          if (list.length > 0) {
            setGenerated(list);
            toast.info(`Showing ${list.length} emails already generated`);
          }
        }
      } else if (res.data.status === 'completed') {
        const list = Object.values(res.data.emails || {});
        setGenerated(list);
        setGenTaskId(res.data.task_id);
        if (res.data.subject) setSubject(res.data.subject);
        setGenerating(false);
        toast.success(`${list.length} emails ready to preview and send!`);
      } else if (res.data.status === 'failed') {
        toast.error('Last generation failed');
      }
    } catch {
      toast.error('Could not check status');
    } finally {
      setCheckingStatus(false);
    }
  }, [subject]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  // Reconnect to an in-progress generation task when panel opens
  useEffect(() => {
    if (!isOpen || generated || !generating) return;
    // If we lost the task_id (page refresh), try to reconnect
    if (generating && !genTaskId) {
      const reconnect = async () => {
        try {
          const res = await axios.get(`${API}/bulk-email/generate-latest`, { withCredentials: true });
          if (res.data.status === 'running' || res.data.status === 'completed') {
            setGenTaskId(res.data.task_id);
            setGenProgress({ generated: res.data.generated || 0, total: res.data.total || 0 });
            if (res.data.status === 'completed') {
              const list = Object.values(res.data.emails || {});
              setGenerated(list);
              if (res.data.subject) setSubject(res.data.subject);
              setGenerating(false);
            }
          }
        } catch {}
      };
      reconnect();
    }
  }, [isOpen, generating, genTaskId, generated]);

  // Speech recognition setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;
      recognition.onresult = (event) => {
        let finalText = '';
        let interimText = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalText += event.results[i][0].transcript;
          } else {
            interimText += event.results[i][0].transcript;
          }
        }
        if (finalText) finalTranscriptRef.current = finalText;
        setInput(finalTranscriptRef.current + (interimText ? ' ' + interimText : ''));
      };
      recognition.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'network') {
          wantsListeningRef.current = false;
          setListening(false);
          toast.error(e.error === 'not-allowed' ? 'Microphone access denied.' : 'Speech recognition requires internet.');
        }
      };
      recognition.onend = () => {
        if (wantsListeningRef.current) {
          try { recognition.start(); } catch { setListening(false); wantsListeningRef.current = false; }
        } else {
          setListening(false);
        }
      };
      recognitionRef.current = recognition;
    }
    return () => { wantsListeningRef.current = false; if (recognitionRef.current) recognitionRef.current.abort(); };
  }, []);

  const toggleVoice = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported');
      return;
    }
    if (listening) {
      wantsListeningRef.current = false;
      recognitionRef.current.stop();
      setListening(false);
    } else {
      finalTranscriptRef.current = input;
      wantsListeningRef.current = true;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.start();
      setListening(true);
    }
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    if (listening) { wantsListeningRef.current = false; recognitionRef.current?.stop(); setListening(false); }

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    finalTranscriptRef.current = '';
    setLoading(true);

    try {
      const res = await axios.post(`${API}/bulk-email/chat-refine`, {
        company_ids: Array.from(selectedCompanyIds),
        message: msg,
        session_id: sessionId || '',
        stage_filter: stageFilter || '',
      }, { withCredentials: true });

      const data = res.data;
      if (!sessionId) setSessionId(data.session_id);
      if (data.subject && !subject) setSubject(data.subject);
      setCurrentDraft(data);
      setMessages(prev => [...prev, {
        role: 'ai',
        email: data,
        version: data.version,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: 'Failed to generate. Try again.', error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!currentDraft) return;
    setSendingTest(true);
    const companyIds = Array.from(selectedCompanyIds);
    const userPrompt = messages.filter(m => m.role === 'user').map(m => m.text).join('\nAdditional instructions: ');
    const testCount = Math.min(5, companyIds.length);
    let sentCount = 0;
    const sentNames = [];

    try {
      // Sample 1: the current draft company
      const name1 = currentDraft.contact_name || 'Sir/Madam';
      const body1 = `Dear ${name1},\n\n${currentDraft.email_body}`;
      await axios.post(`${API}/ai/send-test-email`, {
        subject: `${subject} — ${currentDraft.company_name}`,
        email_body: body1,
      }, { withCredentials: true });
      sentCount++;
      sentNames.push(currentDraft.company_name);

      // Samples 2-5: spread evenly across selected companies
      if (companyIds.length > 1) {
        const step = Math.max(1, Math.floor(companyIds.length / testCount));
        const sampleIndices = [];
        for (let i = 1; i < testCount; i++) {
          const idx = Math.min(i * step, companyIds.length - 1);
          if (!sampleIndices.includes(idx)) sampleIndices.push(idx);
        }

        for (const idx of sampleIndices) {
          try {
            const cid = companyIds[idx];
            const res = await axios.post(`${API}/bulk-email/chat-refine`, {
              company_ids: [cid],
              message: `${userPrompt}\n\nCRITICAL: Follow the EXACT same structure, tone, and **bold** formatting as this approved template — but use THIS company's details only:\n---\n${currentDraft.email_body}\n---`,
              session_id: '',
              stage_filter: stageFilter || '',
            }, { withCredentials: true });
            const name = res.data.contact_name || 'Sir/Madam';
            const body = `Dear ${name},\n\n${res.data.email_body}`;
            const subj = res.data.subject || subject;
            await axios.post(`${API}/ai/send-test-email`, {
              subject: `${subj} — ${res.data.company_name}`,
              email_body: body,
            }, { withCredentials: true });
            sentCount++;
            sentNames.push(res.data.company_name);
          } catch {
            // Skip failed sample, continue with next
          }
        }
      }

      toast.success(`${sentCount} test email${sentCount > 1 ? 's' : ''} sent — ${sentNames.join(', ')}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send test emails');
    } finally {
      setSendingTest(false);
    }
  };

  const approveAndGenerate = async () => {
    if (!currentDraft) return;
    setGenerating(true);
    setGenerated(null);
    setPreviewIdx(0);
    try {
      // Save draft for future reference
      const promptHistory = messages.filter(m => m.role === 'user').map(m => m.text);
      axios.post(`${API}/bulk-email/save-draft`, {
        subject: subject,
        email_body: currentDraft.email_body,
        prompt_history: promptHistory,
        stage_filter: stageFilter || '',
        company_count: selectedCompanyIds.size,
        sample_company: currentDraft.company_name,
        sample_contact: currentDraft.contact_name,
      }, { withCredentials: true }).catch(() => {}); // fire-and-forget

      const fullPrompt = promptHistory.join('\nAdditional instructions: ');

      const res = await axios.post(`${API}/bulk-email/generate-personalized`, {
        company_ids: Array.from(selectedCompanyIds),
        prompt: fullPrompt,
        stage_filter: stageFilter || '',
        approved_template: currentDraft.email_body || '',
        composer: {
          length: composerLength,
          tone: composerTone,
          paragraphs: composerParagraphs,
          cta: composerCta.trim(),
        },
      }, { withCredentials: true });

      const taskId = res.data.task_id;
      setGenTaskId(taskId);
      setGenProgress({ generated: 0, total: res.data.total });

      const poll = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/bulk-email/generate-status/${taskId}`, { withCredentials: true });
          if (st.data.status === 'running') {
            setGenProgress({ generated: st.data.generated, total: st.data.total });
          }
          if (st.data.status === 'completed') {
            clearInterval(poll);
            const list = Object.values(st.data.emails || {});
            setGenerated(list);
            if (st.data.subject && !subject) setSubject(st.data.subject);
            setGenerating(false);
          }
          if (st.data.status === 'failed') {
            clearInterval(poll);
            toast.error('Generation failed');
            setGenerating(false);
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
      setGenerating(false);
    }
  };

  const sendAllEmails = async () => {
    if (!genTaskId || !generated) return;
    setSending(true);
    setSendProgress({ status: 'running', sent: 0, total: generated.length, failed: 0 });
    try {
      const res = await axios.post(`${API}/bulk-email/send-personalized`, {
        generation_task_id: genTaskId,
        subject: subject,
      }, { withCredentials: true });
      toast.success(`Sending ${res.data.total_recipients} personalized emails...`);
      const taskId = res.data.task_id;

      const poll = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/bulk-email/status/${taskId}`, { withCredentials: true });
          setSendProgress({
            status: st.data.status || 'running',
            sent: st.data.sent || 0,
            total: st.data.total || res.data.total_recipients,
            failed: st.data.failed || 0,
          });
          if (st.data.status === 'completed') {
            clearInterval(poll);
            toast.success(`Sent ${st.data.sent} emails${st.data.failed > 0 ? `, ${st.data.failed} failed` : ''}`);
            // Leave the green "All N sent!" bar visible briefly before closing
            setTimeout(() => {
              setSending(false);
              setSendProgress(null);
              if (onSendComplete) onSendComplete();
            }, 1800);
          } else if (st.data.status === 'failed') {
            clearInterval(poll);
            toast.error('Send failed');
            setSending(false);
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
      setSending(false);
      setSendProgress(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-[170px] top-0 h-full w-[960px] bg-slate-50 border-r border-slate-200 shadow-2xl z-[9999] flex flex-col" data-testid="bulk-email-chat">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
            <Mail className="w-3.5 h-3.5 text-teal-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              AI Email Composer
            </p>
            <p className="text-[10px] text-slate-400 truncate">
              {selectedCompanyIds.size} companies selected
              {stageFilter && <span className="text-teal-500"> &middot; {stageFilter} stage</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Check generation status */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[10px] border-teal-200 text-teal-700 hover:bg-teal-50"
            onClick={checkLatestTask}
            disabled={checkingStatus}
            data-testid="check-status-btn"
          >
            {checkingStatus ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Check Status
          </Button>
          {/* Subject line inline */}
          {subject && (
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
              <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">Subject:</span>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="text-xs text-slate-700 bg-transparent border-none outline-none w-[260px]"
                data-testid="subject-input"
              />
            </div>
          )}
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid="close-email-chat">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main content area */}
      {!generated ? (
        <>
          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="email-chat-messages">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-full bg-teal-100 mx-auto mb-3 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-teal-500" />
                </div>
                <p className="text-sm text-slate-500 font-medium">Describe the email you want to send</p>
                <p className="text-[11px] text-slate-400 mt-1 max-w-[360px] mx-auto leading-relaxed">
                  AI will draft a sample email using the first company's {stageFilter || ''} asset data.
                  Refine it through conversation until you're happy, then generate for all {selectedCompanyIds.size} companies.
                </p>
                <div className="mt-4 space-y-1.5 max-w-[380px] mx-auto">
                  {[
                    `I'm attending the BIO Conference and want to discuss licensing their ${stageFilter || ''} asset`,
                    'Introduce ForesightRx and explore partnership opportunities',
                    'Follow up on our previous conversation about their pipeline',
                  ].map((hint, i) => (
                    <button key={i} onClick={() => { setInput(hint); inputRef.current?.focus(); }}
                      className="block w-full text-left text-[11px] text-teal-600 bg-white hover:bg-teal-50 rounded-lg px-3 py-2.5 transition-colors border border-slate-100 hover:border-teal-200 shadow-sm">
                      <ChevronRight className="w-3 h-3 inline mr-1.5 text-teal-400" />{hint}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  <div className="bg-teal-600 text-white rounded-2xl rounded-br-sm px-3.5 py-2.5 max-w-[70%]">
                    <p className="text-[13px] leading-relaxed">{msg.text}</p>
                  </div>
                ) : msg.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[85%]">
                    <p className="text-sm text-red-500">{msg.text}</p>
                  </div>
                ) : msg.email ? (
                  <EmailBubble data={msg.email} version={msg.version} />
                ) : null}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Approve bar */}
          {currentDraft && !loading && !generating && (
            <div className="flex items-center justify-between bg-emerald-50 border-t border-emerald-200 px-4 py-2.5 shrink-0">
              <span className="text-xs text-emerald-700 font-medium">Happy with this version?</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={sendTestEmail}
                  disabled={sendingTest}
                  className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  data-testid="send-test-btn"
                >
                  {sendingTest ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5 mr-1" />}
                  {sendingTest ? 'Sending...' : 'Send 5 Test Emails'}
                </Button>
                <Button
                  size="sm"
                  onClick={approveAndGenerate}
                  className="bg-emerald-600 text-white hover:bg-emerald-700 h-8 text-xs"
                  data-testid="approve-btn"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" /> Approve & Generate All {selectedCompanyIds.size}
                </Button>
              </div>
            </div>
          )}

          {/* Generating spinner */}
          {generating && (
            <div className="flex flex-col items-center justify-center gap-3 bg-slate-50 border-t border-slate-200 px-4 py-4 shrink-0">
              <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">
                  Generating {genProgress.generated} of {genProgress.total} emails...
                </p>
                {genProgress.total > 0 && (
                  <div className="w-48 h-1.5 bg-slate-200 rounded-full mt-2 mx-auto overflow-hidden">
                    <div
                      className="h-full bg-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((genProgress.generated / genProgress.total) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white shrink-0" data-testid="email-chat-input-area">
            {/* AI Composer v2 controls — collapsible so the input stays
                clean by default but power users can tune length / tone /
                paragraphs / CTA before regenerating. */}
            <button
              type="button"
              onClick={() => setComposerOpen(v => !v)}
              className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-800 mb-2"
              data-testid="composer-toggle"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
              Composer controls
              <span className="text-slate-400 normal-case tracking-normal">
                · {composerLength} · {composerTone} · {composerParagraphs}p{composerCta ? ' · custom CTA' : ''}
              </span>
              <span className="ml-auto">{composerOpen ? '▾' : '▸'}</span>
            </button>
            {composerOpen && (
              <div className="grid grid-cols-2 gap-2 mb-3 p-2.5 rounded-lg bg-slate-50 border border-slate-200" data-testid="composer-panel">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Length</label>
                  <select
                    value={composerLength}
                    onChange={e => setComposerLength(e.target.value)}
                    className="w-full mt-1 px-2 py-1 text-[11px] rounded border border-slate-200 bg-white"
                    data-testid="composer-length"
                  >
                    <option value="short">Short (≤90w)</option>
                    <option value="medium">Medium (120-180w)</option>
                    <option value="detailed">Detailed (220-320w)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Tone</label>
                  <select
                    value={composerTone}
                    onChange={e => setComposerTone(e.target.value)}
                    className="w-full mt-1 px-2 py-1 text-[11px] rounded border border-slate-200 bg-white"
                    data-testid="composer-tone"
                  >
                    <option value="formal">Formal</option>
                    <option value="friendly">Friendly</option>
                    <option value="executive">Executive</option>
                    <option value="scientific">Scientific</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Paragraphs</label>
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={composerParagraphs}
                    onChange={e => setComposerParagraphs(Math.max(1, Math.min(8, parseInt(e.target.value) || 3)))}
                    className="w-full mt-1 px-2 py-1 text-[11px] rounded border border-slate-200 bg-white"
                    data-testid="composer-paragraphs"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">CTA (optional)</label>
                  <input
                    type="text"
                    value={composerCta}
                    onChange={e => setComposerCta(e.target.value)}
                    placeholder="e.g. Reply for a 20-min call"
                    className="w-full mt-1 px-2 py-1 text-[11px] rounded border border-slate-200 bg-white"
                    data-testid="composer-cta"
                  />
                </div>
              </div>
            )}
            {listening && (
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[10px] text-red-500 font-medium">Listening...</span>
              </div>
            )}
            <div className="flex items-end gap-2">
              <button
                onClick={toggleVoice}
                className={`p-2 rounded-lg transition-all border shrink-0 ${listening ? 'bg-red-500 text-white border-red-500 hover:bg-red-600' : 'bg-slate-50 text-slate-400 border-slate-100 hover:bg-slate-100 hover:text-slate-600'}`}
                data-testid="voice-btn"
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder={messages.length === 0 ? 'Describe the email you want to send...' : 'Make it shorter, add conference details, change tone...'}
                rows={Math.max(3, Math.min(input.split('\n').length, 6))}
                className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-[13px] text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 transition-all"
                style={{ minHeight: '80px', maxHeight: '180px' }}
                disabled={loading || generating}
                data-testid="email-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading || generating}
                className="p-2.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm shrink-0"
                data-testid="send-msg-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      ) : (
        /* Generated emails preview */
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {generated.length} Personalized Emails Ready
            </h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setGenerated(null); setGenTaskId(null); }} data-testid="back-to-chat">
                <RefreshCw className="w-3 h-3 mr-1" /> Back to Chat
              </Button>
            </div>
          </div>

          {/* Preview card */}
          <div className="flex-1 overflow-y-auto bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400">Preview {previewIdx + 1} of {generated.length}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setPreviewIdx(Math.max(0, previewIdx - 1))} disabled={previewIdx === 0} data-testid="prev-email"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setPreviewIdx(Math.min(generated.length - 1, previewIdx + 1))} disabled={previewIdx >= generated.length - 1} data-testid="next-email"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
            {generated[previewIdx] && (
              <>
                <div className="text-xs text-slate-500 border-b border-slate-100 pb-2 mb-3 space-y-0.5">
                  <p><span className="font-medium text-slate-700">{generated[previewIdx].company_name}</span></p>
                  <p>To: {generated[previewIdx].contact_name} &lt;{generated[previewIdx].contact_email}&gt;</p>
                  {generated[previewIdx].subject && (
                    <p>Subject: <span className="font-medium text-slate-700">{generated[previewIdx].subject}</span></p>
                  )}
                </div>
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif" }}>
                  Dear {generated[previewIdx].contact_name || 'Sir/Madam'},{'\n\n'}
                  {generated[previewIdx].email_body}
                </p>
              </>
            )}
          </div>

          {/* Send bar */}
          <div className="space-y-2 shrink-0">
            {sendProgress && (
              <BulkSendProgress
                progress={sendProgress}
                label="Sending personalized emails…"
                testId="personalized-send-progress"
                onDone={() => { setSendProgress(null); if (onClose) onClose(); }}
              />
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Mail className="w-3.5 h-3.5" />
                <span className="text-slate-400">Each email has a personalized subject</span>
              </div>
              <Button
                onClick={sendAllEmails}
                disabled={sending}
                className="bg-teal-600 text-white hover:bg-teal-700"
                data-testid="send-all-btn"
              >
                {sending ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-3.5 h-3.5 mr-1.5" /> Send {generated.length} Emails</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
